import type { ReactNode } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { Card } from '@shared/ui/Card';
import { IconArrowRight } from '@shared/ui/icons';

/* Общие детали карточек дашборда объекта: каркас блока, крупные цифры, полосы
   прогресса, мини-гистограммы. Всё на theme-токенах — работает в обеих темах. */

export const BlockCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
  padding: ${({ theme }) => theme.spacing(5)};
  min-width: 0;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const HeadMain = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  min-width: 0;
`;

const HeadIcon = styled.span`
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: ${({ theme }) => theme.radius.md};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primarySoft};
`;

const HeadTitle = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const HeadSource = styled.div`
  margin-top: 2px;
  font-size: 11.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const HeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  flex-shrink: 0;
`;

interface BlockHeadProps {
  icon: ReactNode;
  title: string;
  source: string;
  actions?: ReactNode;
}

/** Шапка блока: иконка, название, источник данных и действия справа. */
export function BlockHead({ icon, title, source, actions }: BlockHeadProps) {
  return (
    <Head>
      <HeadMain>
        <HeadIcon aria-hidden>{icon}</HeadIcon>
        <div>
          <HeadTitle>{title}</HeadTitle>
          <HeadSource>{source}</HeadSource>
        </div>
      </HeadMain>
      {actions && <HeadActions>{actions}</HeadActions>}
    </Head>
  );
}

export const StatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
`;

type Tone = 'neutral' | 'success' | 'warning' | 'danger';

const StatBox = styled.div<{ $tone: Tone }>`
  padding: ${({ theme }) => `${theme.spacing(3)} ${theme.spacing(3.5)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  ${({ $tone, theme }) =>
    $tone !== 'neutral' &&
    css`
      border-color: ${
        $tone === 'success'
          ? theme.colors.status.success
          : $tone === 'warning'
            ? theme.colors.status.warning
            : theme.colors.status.danger
      };
    `}
`;

const StatValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const StatLabel = styled.div`
  margin-top: ${({ theme }) => theme.spacing(1)};
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const StatHint = styled.div`
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

interface StatProps {
  value: ReactNode;
  label: string;
  hint?: ReactNode;
  tone?: Tone;
}

/** Крупная цифра блока с подписью и необязательной подсказкой. */
export function Stat({ value, label, hint, tone = 'neutral' }: StatProps) {
  return (
    <StatBox $tone={tone}>
      <StatValue>{value}</StatValue>
      <StatLabel>{label}</StatLabel>
      {hint && <StatHint>{hint}</StatHint>}
    </StatBox>
  );
}

const grow = keyframes`
  from { transform: scaleX(0); }
  to   { transform: scaleX(1); }
`;

const rise = keyframes`
  from { transform: scaleY(0); }
  to   { transform: scaleY(1); }
`;

const BarTrack = styled.div`
  position: relative;
  height: 8px;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
`;

const BarFill = styled.div<{ $pct: number; $tone: 'primary' | 'success' | 'warning' }>`
  position: absolute;
  inset: 0 auto 0 0;
  width: ${({ $pct }) => `${Math.max(0, Math.min(100, $pct))}%`};
  border-radius: inherit;
  background: ${({ $tone, theme }) =>
    $tone === 'success'
      ? theme.colors.status.success
      : $tone === 'warning'
        ? theme.colors.status.warning
        : theme.colors.primary};
  transform-origin: left center;
  animation: ${grow} 700ms ${({ theme }) => theme.motion.easeOut} both;
`;

/** Полоса заполнения 0–100 %. */
export function Bar({
  pct,
  tone = 'primary',
  label,
}: {
  pct: number | null;
  tone?: 'primary' | 'success' | 'warning';
  label: string;
}) {
  const v = pct ?? 0;
  return (
    <BarTrack
      role="meter"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(v)}
      aria-label={label}
    >
      <BarFill $pct={v} $tone={tone} />
    </BarTrack>
  );
}

const Columns = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(1.5)};
  height: 96px;
  padding-top: ${({ theme }) => theme.spacing(2)};
`;

const Column = styled.div`
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-end;
  gap: 2px;
`;

const ColumnBars = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
`;

const ColumnBar = styled.div<{ $h: number; $second?: boolean }>`
  width: 100%;
  max-width: 18px;
  height: ${({ $h }) => `${Math.max($h, 0)}%`};
  min-height: ${({ $h }) => ($h > 0 ? '2px' : '0')};
  border-radius: 3px 3px 0 0;
  background: ${({ $second, theme }) => ($second ? theme.colors.text.muted : theme.colors.primary)};
  opacity: ${({ $second }) => ($second ? 0.55 : 0.9)};
  transform-origin: bottom center;
  animation: ${rise} 600ms ${({ theme }) => theme.motion.easeOut} both;
`;

const ColumnLabel = styled.div`
  font-size: 10.5px;
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export interface ColumnDatum {
  key: string;
  label: string;
  a: number;
  b?: number;
  title: string;
}

/** Мини-гистограмма по месяцам: одна или две серии (основная — цвет бренда). */
export function MiniColumns({ data, ariaLabel }: { data: ColumnDatum[]; ariaLabel: string }) {
  const max = Math.max(1, ...data.map((d) => Math.max(d.a, d.b ?? 0)));
  return (
    <Columns role="img" aria-label={ariaLabel}>
      {data.map((d) => (
        <Column key={d.key} title={d.title}>
          <ColumnBars>
            <ColumnBar $h={(d.a / max) * 100} />
            {d.b !== undefined && <ColumnBar $second $h={(d.b / max) * 100} />}
          </ColumnBars>
          <ColumnLabel>{d.label}</ColumnLabel>
        </Column>
      ))}
    </Columns>
  );
}

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

export const LegendDot = styled.span<{ $second?: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
  background: ${({ $second, theme }) => ($second ? theme.colors.text.muted : theme.colors.primary)};
  opacity: ${({ $second }) => ($second ? 0.55 : 0.9)};
`;

export const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

export const ListItem = styled.li`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.colors.surface};
  font-size: 12.5px;
  min-width: 0;
`;

export const ItemMain = styled.div`
  min-width: 0;
  flex: 1;
`;

export const ItemTitle = styled.div`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ItemMeta = styled.div`
  margin-top: 2px;
  color: ${({ theme }) => theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

export const Muted = styled.p`
  margin: 0;
  font-size: 12.5px;
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const FootLink = styled(Link)`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: auto;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }
  &:hover > svg {
    transform: translateX(3px);
  }
  &:focus-visible {
    outline: 2px solid transparent;
    box-shadow: ${({ theme }) => theme.shadow.focus};
    border-radius: ${({ theme }) => theme.radius.sm};
  }
`;

/** Ссылка в агент-источник внизу блока. */
export function SourceLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <FootLink to={to}>
      {children}
      <IconArrowRight size={14} />
    </FootLink>
  );
}
